import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Modal, SvgIcon, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import moment from 'moment';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import DSNSummaryModal from 'src/components/dailySessionNotes/DSN summary/DSNSummaryModal';
import loader from 'src/components/LoaderImg/loader.gif';
import ReForm from 'src/components/reform';
import AddendumNote from 'src/components/AddendumNote';
import SucessModal from 'src/components/sucess-modal';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import useCurrentUser from 'src/hooks/use-current-user';
import { paths } from 'src/paths';
import {
  checkIsReformAction,
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { getSinglePatient } from 'src/redux/actions/patientAction';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import { checkSupervisorPermission } from 'src/utils/checkSignatureAccess';
import isEvalFormType from 'src/utils/isEvalFormType';
import DocumentTemplateUnavailable from 'src/components/document-management/document-template-unavailable';
import SubmitNotifyAssistantModal from './submit-notify-assistant-modal';

type Props = Record<string, never>;

const loaderCSS: CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  userSelect: 'none',
  zIndex: 9999,
};

function TherapistDashboardDocEdit(props: Props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isSupervisorEdit = location?.state?.isSupervisorEdit || false;
  const document = useSelector(
    (state: any) => state.dashboardCreateEditDocumentDataReducer.document
  );
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const checkpatientLoading = useSelector((state: any) => state?.getSinglePatientReducer?.loading);
  const [initialized, setInitialized] = useState(false);
  const [showPayerWarningModal, setShowPayerWarningModal] = useState(false);
  const [showSubmitNotifyModal, setShowSubmitNotifyModal] = useState(false);
  const submitNotifyResolverRef = useRef<
    ((result: { proceed: boolean; note?: string }) => void) | null
  >(null);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);

  const handleBeforeSupervisorSubmit = (): Promise<{ proceed: boolean; note?: string }> => {
    return new Promise((resolve) => {
      submitNotifyResolverRef.current = resolve;
      setShowSubmitNotifyModal(true);
    });
  };

  const handleSubmitNotifyCancel = () => {
    setShowSubmitNotifyModal(false);
    submitNotifyResolverRef.current?.({ proceed: false });
    submitNotifyResolverRef.current = null;
  };

  const handleSubmitNotifyConfirm = (note: string) => {
    setShowSubmitNotifyModal(false);
    submitNotifyResolverRef.current?.({ proceed: true, note });
    submitNotifyResolverRef.current = null;
  };

  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);
  const getSignatureAccess = useSelector((state: any) => state.getSignatureAccessReducer);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);

  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  useEffect(() => {
    if (!initialized) {
      dispatch(resetUuidAction());
      setInitialized(true);
    }
  }, [initialized, dispatch]);

  const useReformAddendumNote = document?.formType === 'ADDENDUM_NOTE_REFORM';

  const handlePayerError = (error: {
    error?: string;
    errorCode?: string;
    errorData?: any;
    episodeOfCareId?: string;
  }) => {
    if (error.errorCode === 'NO_PAYER_ON_CERTIFICATION_START_DATE') {
      setShowPayerWarningModal(true);
    }
  };

  const handleCancelPayerModal = () => {
    setShowPayerWarningModal(false);
    navigate(paths.dashboard);
  };

  const handleAssignPayer = () => {
    setShowPayerWarningModal(false);
    navigate(`${paths.patients}/${paths.patientDetails}${paths.payers}`, {
      state: {
        patientId: getPatientId,
      },
    });
  };

  const isReformsEnabled = () => {
    if (!pullUuidResponse?.response || !document.reformId) {
      return false;
    }

    if (document.formType === 'EVALUATION_REFORM_PT') {
      return true;
    }

    if (document.formType === 'EVALUATION_REFORM_OT') {
      return true;
    }

    if (document.formType === 'EVALUATION_REFORM_SLP') {
      return true;
    }

    if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_PT') {
      return true;
    }

    if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_OT') {
      return true;
    }

    if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_SLP') {
      return true;
    }

    if (document.formType === 'DAILY_SOAP_NOTE_REFORM') {
      return true;
    }

    if (document.formType === 'RECERTIFICATION_REFORM_OT') {
      return true;
    }

    if (document.formType === 'RECERTIFICATION_REFORM_PT') {
      return true;
    }

    if (document.formType === 'RECERTIFICATION_REFORM_SLP') {
      return true;
    }
    if (document.formType === 'DISCHARGE_REFORM_OT') {
      return true;
    }
    if (document.formType === 'DISCHARGE_REFORM_PT') {
      return true;
    }
    if (document.formType === 'DISCHARGE_REFORM_SLP') {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!document) return;
    dispatch(checkIsReformAction(document?.formType?.includes('REFORM')));
  }, [document]);

  const getCleanUuid = () => {
    return pullUuidResponse?.response?.trim() || '';
  };

  const backToEOCDetails = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    // A new document (no submission yet) needs the patient for the create view.
    if (!document || !pullUuidResponse.response || document?.submissionId) return;
    dispatch(
      getSinglePatient({
        patientId: document?.patientId,
      })
    );
  }, [document, pullUuidResponse]);

  useEffect(() => {
    if (!document) return;

    if (
      document?.formType === 'DAILY_SOAP_NOTE' ||
      document?.formType === 'DAILY_SOAP_NOTE_REFORM'
    ) {
      dispatch(
        pullUuidAction({
          notesId: document?.dailySessionNoteUuid,
          entityType: 'SOAP',
          formId: document?.formId,
        })
      );
    } else {
      let fetchMostRecent = false;

      if (document.reformId) {
        if (document.formType === 'EVALUATION_REFORM_PT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'EVALUATION_REFORM_OT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'EVALUATION_REFORM_SLP') {
          fetchMostRecent = true;
        }

        if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_PT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_OT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_SLP') {
          fetchMostRecent = true;
        }

        if (document.formType === 'DAILY_SOAP_NOTE_REFORM') {
          fetchMostRecent = true;
        }

        if (document.formType === 'RECERTIFICATION_REFORM_OT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'RECERTIFICATION_REFORM_PT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'RECERTIFICATION_REFORM_SLP') {
          fetchMostRecent = true;
        }

        if (document.formType === 'DISCHARGE_REFORM_OT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'DISCHARGE_REFORM_PT') {
          fetchMostRecent = true;
        }

        if (document.formType === 'DISCHARGE_REFORM_SLP') {
          fetchMostRecent = true;
        }
      }

      dispatch(
        pullUuidAction(
          {
            uuid: document?.submissionId ? document.uuid : null,
            providerId,
            formId: document?.formId,
            notesId:
              document?.formType === 'DAILY_SOAP_NOTE' ||
              document?.formType === 'DAILY_SOAP_NOTE_REFORM'
                ? document.uuid
                : null,
            episodeOfCareId: document?.episodeOfCareId,
            type: discipline,
            isForSupervisor: checkSupervisorPermission(
              getSignatureAccess.response?.data?.t1,
              document?.formType
            ),
            entityType: 'EOC',
            documentType: document?.formType,
            fromRequired: document.submissionId ? false : true,
          },
          {
            fetchMostRecent,
          }
        )
      );
    }
  }, [document]);

  const closeErrorModal = () => {
    setOpenErrorModal(false);
    dispatch(resetForLoaderDocumentation());
  };

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (isDocLoader.error) {
      setOpenErrorModal(true);
      return;
    }
    if (!isDocLoader.loader) {
      dispatch(resetForLoaderDocumentation());
      navigate('/dashboard');
    }
  }, [isDocLoader]);

  const handleOpenDsnSummary = () => {
    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: document?.patientId,
        type: 'progress-note',
        params: {
          discipline: document?.type,
          localDate: document?.dueDate
            ? document?.dueDate
            : moment(new Date()).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  return (
    <>
      {isDocLoader.loader && (
        <Grid sx={loaderCSS}>
          <img
            src={loader}
            alt="loader.."
          />
        </Grid>
      )}

      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={backToEOCDetails}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          {/* <Link
            style={{ color: '#111927' }}
            to={'/dashboard'}
          > */}
          <Typography variant="subtitle1">Back to Dashboard</Typography>
          {/* </Link> */}
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack>
            {document?.patientName && <Typography variant="h6">{document?.patientName}</Typography>}
            <Typography variant="h5">{`${
              document?.submissionId ? 'Edit Document' : 'Create Document'
            } - ${document.documentType}`}</Typography>
          </Stack>
          <Stack>
            {document.formType?.includes('WEEKLY_PROGRESS_NOTE') && (
              <Button
                color="primary"
                size="small"
                onClick={() => handleOpenDsnSummary()}
                variant="outlined"
              >
                DSN Summary
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Box
        sx={{ height: '100%' }}
        px={3}
      >
        {document?.submissionId ? (
          <Box
            sx={{ height: '100%' }}
            px={3}
          >
            {isReformsEnabled() ? (
              <Box
                style={
                  document?.formType === 'DAILY_SOAP_NOTE_REFORM'
                    ? undefined
                    : { maxHeight: '70vh', overflow: 'auto' }
                }
              >
                <ReForm
                  uuid={getCleanUuid()}
                  formId={document?.reformId}
                  onSubmitError={handlePayerError}
                  isSupervisorEdit={isSupervisorEdit}
                  onBeforeSupervisorSubmit={
                    isSupervisorEdit ? handleBeforeSupervisorSubmit : undefined
                  }
                  // THIN-8038: the author needs a supervisor on this document type, so let the
                  // SOAP-note Supervisor field offer the facility's eligible supervisors. A
                  // cosigner editing the note is the supervisor, so they get the manual fields.
                  supervisorSelectionEnabled={
                    !isSupervisorEdit &&
                    checkSupervisorPermission(
                      getSignatureAccess.response?.data?.t1,
                      document?.formType
                    )
                  }
                />
              </Box>
            ) : useReformAddendumNote ? (
              <AddendumNote
                formId={document?.reformId}
                webhookUuid={getCleanUuid()}
                patientData={patientData}
                loadPrefill={Boolean(document?.submissionId)}
              />
            ) : isEvalFormType(document?.formType) && pullUuidResponse.response ? (
              // BRK-1921: evaluations are reform-only — never fall back to the JotForm prefill.
              // BRK-2211: gated on the minted uuid so it doesn't flash while that is loading.
              <DocumentTemplateUnavailable
                onBack={backToEOCDetails}
                backLabel="Back to Dashboard"
              />
            ) : pullUuidResponse.response ? (
              // Jotform is gone: a legacy Jotform document (it carries a prefillUrl) can no longer
              // be edited, and a Reform type missing its reformId is a template misconfiguration.
              <DocumentTemplateUnavailable
                onBack={backToEOCDetails}
                backLabel="Back to Dashboard"
                reason={document?.prefillUrl ? 'legacy' : 'unconfigured'}
              />
            ) : null}
          </Box>
        ) : (
          <>
            {checkpatientLoading.isLoading ? (
              <img
                src={loader}
                alt="loader"
                style={{ width: '10%', margin: 'auto', display: 'block', marginTop: '10%' }}
              />
            ) : (
              <Box
                sx={{ height: '100%' }}
                px={3}
              >
                {isReformsEnabled() ? (
                  <Box
                    style={
                      document?.formType === 'DAILY_SOAP_NOTE_REFORM'
                        ? undefined
                        : { maxHeight: '70vh', overflow: 'auto' }
                    }
                  >
                    <ReForm
                      uuid={getCleanUuid()}
                      formId={document?.reformId}
                      onSubmitError={handlePayerError}
                      supervisorSelectionEnabled={checkSupervisorPermission(
                        getSignatureAccess.response?.data?.t1,
                        document?.formType
                      )}
                    />
                  </Box>
                ) : useReformAddendumNote ? (
                  <AddendumNote
                    formId={document?.reformId}
                    webhookUuid={getCleanUuid()}
                    patientData={patientData}
                    loadPrefill={Boolean(document?.submissionId)}
                  />
                ) : isEvalFormType(document?.formType) && pullUuidResponse.response ? (
                  // BRK-1921: evaluations are reform-only — never fall back to the JotForm iframe.
                  // BRK-2211: gated on the minted uuid so it doesn't flash while that is loading.
                  <DocumentTemplateUnavailable
                    onBack={backToEOCDetails}
                    backLabel="Back to Dashboard"
                  />
                ) : pullUuidResponse.response ? (
                  // Jotform is gone: a type that can't render natively (a legacy Jotform type
                  // such as ADDENDUM_NOTE, or a Reform type missing its reformId) dead-ends here.
                  <DocumentTemplateUnavailable
                    onBack={backToEOCDetails}
                    backLabel="Back to Dashboard"
                  />
                ) : null}
              </Box>
            )}
          </>
        )}

        <Modal open={openErrorModal}>
          <SucessModal
            close={closeErrorModal}
            isSucess={false}
            title="Edit a Document"
            message={isDocLoader.response}
          />
        </Modal>
      </Box>
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${discipline}`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
      <WarningModal
        open={showPayerWarningModal}
        onClose={handleCancelPayerModal}
        title="Payer Required"
        message="A certification must be associated with an active payer. Please assign a payer for the selected date before continuing."
        buttonText="Assign Payer"
        cancelBtn="Cancel"
        handleSaveBtn={handleAssignPayer}
      />
      <SubmitNotifyAssistantModal
        open={showSubmitNotifyModal}
        onCancel={handleSubmitNotifyCancel}
        onSubmit={handleSubmitNotifyConfirm}
      />
    </>
  );
}

export default TherapistDashboardDocEdit;
